import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { NotificationBell } from './NotificationBell';
import {
  House,
  FileText,
  CheckCircle,
  Package,
  Truck,
  Users,
  Gear,
  ChartBar,
  SignOut,
  List,
  X,
  Trash,
  Wrench,
  UserSwitch,
  Storefront,
  LockKey,
  Cube,
  Warehouse,
  UsersThree,
  DeviceMobile,
  CurrencyInr,
  ChatCircleDots,
  BellRinging,
  Receipt,
  CalendarCheck,
  CalendarBlank,
  Factory,
  PlugsConnected
} from '@phosphor-icons/react';

const MENU_ITEMS = [
  // Admin menus
  { path: '/dashboard', label: 'Dashboard', icon: House, roles: ['admin'] },
  { path: '/job-orders', label: 'Job Orders', icon: FileText, roles: ['executive', 'admin'] },
  { path: '/approved-jobs', label: 'Approved JO', icon: CheckCircle, roles: ['admin', 'procurement'] },
  { path: '/procurement', label: 'Procurement', icon: Package, roles: ['procurement', 'admin'] },
  { path: '/procurement/reports', label: 'Procurement Reports', icon: ChartBar, roles: ['procurement', 'admin'] },
  { path: '/admin/production-planning', label: 'Production Planner', icon: CalendarCheck, roles: ['admin'] },
  { path: '/admin/delivery-calendar', label: 'Delivery Calendar', icon: CalendarBlank, roles: ['admin'] },
  { path: '/production', label: 'Production', icon: Wrench, roles: ['production', 'admin'] },
  { path: '/production/reports', label: 'Production Reports', icon: ChartBar, roles: ['production'] },
  { path: '/installation', label: 'Installation / Dispatch', icon: Truck, roles: ['installation', 'admin'] },
  { path: '/reports', label: 'Reports', icon: ChartBar, roles: ['admin'] },
  { path: '/admin/mobile-quotations', label: 'Mobile Quotations', icon: DeviceMobile, roles: ['admin', 'sales'] },
  { path: '/admin/customers', label: 'Customer Master', icon: Users, roles: ['admin', 'sales'] },
  { path: '/admin/mobile-job-orders', label: 'Mobile Job Orders', icon: DeviceMobile, roles: ['admin', 'sales'] },
  { path: '/admin/outstanding-report', label: 'Outstanding Report', icon: CurrencyInr, roles: ['admin'] },
  { path: '/admin/receipts', label: 'Payment Receipts', icon: Receipt, roles: ['admin'] },
  { path: '/admin/procurement', label: 'Procurement', icon: Factory, roles: ['procurement'] },
  { path: '/admin/notifications', label: 'WhatsApp Log', icon: ChatCircleDots, roles: ['admin'] },
  { path: '/admin/notification-settings', label: 'Auto-Notify Rules', icon: BellRinging, roles: ['admin'] },
  { path: '/admin/integrations', label: 'Integrations', icon: PlugsConnected, roles: ['admin'] },
  { path: '/admin/company-settings', label: 'Company Settings', icon: Gear, roles: ['admin'] },
  { path: '/deleted-jobs', label: 'Deleted JO', icon: Trash, roles: ['admin'] },
  { path: '/users', label: 'Users', icon: Users, roles: ['admin'] },
  { path: '/settings', label: 'Settings', icon: Gear, roles: ['admin'] },
  // Inventory (inventory role only)
  { path: '/inventory', label: 'Inventory', icon: Cube, roles: ['inventory'] },
  { path: '/inventory/masters', label: 'Inv Masters', icon: Package, roles: ['inventory'] },
  { path: '/inventory/job-orders', label: 'Inv Job Orders', icon: FileText, roles: ['inventory'] },
  { path: '/inventory/stock', label: 'Stock Ops', icon: Warehouse, roles: ['inventory'] },
  { path: '/inventory/procurement', label: 'Procurement Req', icon: ChartBar, roles: ['inventory'] },
  { path: '/inventory/reports', label: 'Inv Reports', icon: ChartBar, roles: ['inventory'] },
  // Installation Team menus
  { path: '/installation', label: 'Dashboard', icon: House, roles: ['installation'], exact: true },
  { path: '/installation/dispatch', label: 'Dispatch Management', icon: Truck, roles: ['installation'] },
  { path: '/installation/reports', label: 'Reports', icon: ChartBar, roles: ['installation'] },
  // Sales Team menus
  { path: '/sales', label: 'Dashboard', icon: Storefront, roles: ['sales'] },
];

export function Layout({ children }) {
  const { user, logout, switchUser } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const filteredMenuItems = MENU_ITEMS.filter(item => {
    if (!user) return false;
    if (!item.roles.includes(user.role)) return false;
    // For installation role, only show installation-specific items
    if (user.role === 'installation' && !item.path.startsWith('/installation') && item.path !== '/job-orders') return false;
    // For sales role, only show sales-specific items + mobile quotations/JO + customer master (read-only)
    if (user.role === 'sales' && !item.path.startsWith('/sales') && !item.path.startsWith('/admin/mobile-') && item.path !== '/admin/customers') return false;
    // For admin, skip installation-only Dashboard duplicate
    if (user.role === 'admin' && item.exact && item.path === '/installation') return false;
    // For inventory role, only show inventory-specific items
    if (user.role === 'inventory' && !item.path.startsWith('/inventory') && item.path !== '/job-orders') return false;
    return true;
  });

  return (
    <div className="h-screen bg-zinc-100 flex overflow-hidden">
      {/* Mobile Header Bar - Fixed */}
      <div className="fixed top-0 left-0 right-0 z-50 lg:hidden bg-zinc-950 h-14 flex items-center px-4 shadow-lg">
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white p-1.5 rounded-sm"
        >
          {sidebarOpen ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
        </button>
        <div className="flex items-center gap-2 ml-3">
          <div className="w-7 h-7 bg-orange-500 rounded-sm flex items-center justify-center text-white font-black text-xs">K</div>
          <span className="text-sm font-bold tracking-[0.1em] uppercase text-white">Karthik</span>
        </div>
        {user && (
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <span className="text-xs font-bold text-zinc-400 uppercase">{user.role}</span>
          </div>
        )}
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Always Fixed */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-zinc-950 text-white transform transition-transform duration-200 ease-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 flex-shrink-0 flex flex-col`}>
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-500 rounded-sm flex items-center justify-center text-white font-black text-sm">K</div>
            <div>
              <h1 className="text-sm font-bold tracking-[0.15em] uppercase text-white">Karthik</h1>
              <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Doors & Windows</p>
            </div>
          </div>
          {user && (
            <div className="mt-4 p-3 bg-zinc-900 rounded-sm">
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em]">{user.role}</p>
              <p className="text-sm text-white mt-0.5">{user.name}</p>
            </div>
          )}
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {filteredMenuItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={`${item.path}-${item.label}`}
                to={item.path}
                data-testid={`nav-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-bold transition-colors ${
                  isActive
                    ? 'bg-orange-500 text-white'
                    : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                }`}
              >
                <Icon size={20} weight={isActive ? 'bold' : 'regular'} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-zinc-800 space-y-0.5 flex-shrink-0">
          <button
            data-testid="change-password-button"
            onClick={() => setShowChangePassword(true)}
            className="flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-bold text-zinc-400 hover:bg-amber-900/50 hover:text-amber-400 w-full transition-colors"
          >
            <LockKey size={20} />
            Change Password
          </button>
          <button
            data-testid="switch-user-button"
            onClick={switchUser}
            className="flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-bold text-zinc-400 hover:bg-blue-900/50 hover:text-blue-400 w-full transition-colors"
          >
            <UserSwitch size={20} />
            Switch User
          </button>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-bold text-zinc-400 hover:bg-red-900/50 hover:text-red-400 w-full transition-colors"
          >
            <SignOut size={20} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content - Scrollable */}
      <main className="flex-1 min-w-0 lg:ml-64 overflow-y-auto h-screen pt-14 lg:pt-0">
        <div className="p-4 lg:p-8 max-w-7xl mx-auto pb-8">
          {children}
        </div>
      </main>

      <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />
    </div>
  );
}
